'use client';

import { useEffect, useRef } from 'react';
import { useDebugTracker } from './use-debug-tracker';
import { useAuth } from '@/providers/auth-provider';

export function DebugInitializer() {
  const { addDebugLog, clearLogs, forceHideDebug } = useDebugTracker();
  const { user, isAuthenticated } = useAuth();
  const lastUrlRef = useRef<string>('');
  const isInitializedRef = useRef(false);
  const lastUserStateRef = useRef<{ user: any; isAuthenticated: boolean }>({
    user: null,
    isAuthenticated: false,
  });

  // One-time setup - expose debug tracker to window
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Expose debug tracker to window for auth provider to use
    (window as any).ahafoodDebugTracker = {
      addDebugLog,
      clearLogs,
      forceHideDebug,
    };
  }, [addDebugLog, clearLogs, forceHideDebug]);

  // One-time initialization and URL tracking setup
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Log initial page load only once
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      lastUrlRef.current = window.location.href;

      addDebugLog({
        url: window.location.href,
      });
    }

    // Track URL changes for SPA navigation
    const handleUrlChange = () => {
      const currentUrl = window.location.href;

      // Only log if URL actually changed
      if (currentUrl !== lastUrlRef.current) {
        lastUrlRef.current = currentUrl;

        addDebugLog({
          url: currentUrl,
        });
      }
    };

    // Override history methods with throttling
    const originalPushState = history.pushState.bind(history);
    const originalReplaceState = history.replaceState.bind(history);

    history.pushState = function (...args) {
      originalPushState(...args);
      // Use setTimeout to avoid spam during rapid navigation
      setTimeout(handleUrlChange, 100);
    };

    history.replaceState = function (...args) {
      originalReplaceState(...args);
      setTimeout(handleUrlChange, 100);
    };

    // Listen for popstate (back/forward buttons)
    const handlePopState = () => {
      setTimeout(handleUrlChange, 100);
    };

    window.addEventListener('popstate', handlePopState);

    // Listen for page visibility change (when user comes back to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setTimeout(handleUrlChange, 200);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [addDebugLog]); // addDebugLog is now stable with useCallback

  // Ghi customerId vào debug log khi user resolve. BẮT BUỘC để panel bật đúng
  // target — `isTargetUser` so log.customerId với TARGET_CUSTOMER_ID. Dùng
  // `cunstomerId` (= JWT cid, format 84xxxxxxxxx). Chỉ log 1 lần / customerId.
  const loggedCustomerIdRef = useRef<string | null>(null);
  useEffect(() => {
    const customerId = user?.cunstomerId ?? user?.phone;
    if (!isAuthenticated || !customerId) return;
    if (loggedCustomerIdRef.current === customerId) return;
    loggedCustomerIdRef.current = customerId;
    addDebugLog({ customerId });
  }, [user, isAuthenticated, addDebugLog]);

  // Monitor user state changes with debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      const currentUserState = { user, isAuthenticated };

      // Check if user state actually changed
      if (
        lastUserStateRef.current.user !== user ||
        lastUserStateRef.current.isAuthenticated !== isAuthenticated
      ) {
        const wasAuthenticated =
          lastUserStateRef.current.user &&
          lastUserStateRef.current.isAuthenticated;
        const isNowAuthenticated = user && isAuthenticated;

        lastUserStateRef.current = currentUserState;

        // Only clear logs if user goes from authenticated to not authenticated
        if (wasAuthenticated && !isNowAuthenticated) {
          clearLogs();
          forceHideDebug();
        }
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [user, isAuthenticated, clearLogs, forceHideDebug]);

  return null; // This component doesn't render anything
}
